import React from 'react';
import { Typography } from '../../../interfaces/typography';
import { CustomDefinition, CustomMetric, ConversationType } from "../../../interfaces/db";
import { TypedResultMap } from "../../../interfaces/sessions";
import { CustomDetail } from "../../../interfaces/db";
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { Check, X, ChevronUp, ChevronDown } from 'lucide-react';
import { useState, useRef } from 'react';
import { formatCustomScore, FormattedCustomScore } from '../../../lib/redux/sessions/custom-scores/custom-score-formatter-hook';
import { getCustomCode } from '../../CustomScores/cfg';
import { convertFromReduxSafeCustomDefinitions } from '../../../lib/redux/users/slice';

interface ScoreMetricProps {
    customDetail: CustomDetail | null
    customMetric: CustomMetric | null
    teamId?: string
}

export const ScoreMetric: React.FC<ScoreMetricProps> = ({ 
    customDetail, 
    customMetric, 
    teamId 
}) => {
    const reduxSafeDefinitions = useSelector((state: RootState) => state.customDefinitions.value) ?? [];
    const customDefinitions = reduxSafeDefinitions.length > 0 ? convertFromReduxSafeCustomDefinitions(reduxSafeDefinitions) : [];
    const teamCustomCodes = getCustomCode(teamId || '');
    const [hoveredMetric, setHoveredMetric] = useState<number | null>(null);
    const elementRefs = useRef<Record<number, HTMLDivElement | null>>({});

    const getFormattedMetric = (def: CustomDefinition): FormattedCustomScore | null => {
        if (!customMetric || !customDetail || def.metric_idx === null) {
            return null;
        }
        
        const metricKey = `metric_${def.metric_idx}` as keyof CustomMetric;
        const rawValue = customMetric[metricKey];

        let explanation: string | null = null;
        try {
            if (customDetail.metric_to_explanation_json_str) {
                const explanations = JSON.parse(customDetail.metric_to_explanation_json_str) as Record<string, string>;
                explanation = explanations[def.metric_idx.toString()] || null;
            }
        } catch (e) {
            console.error('Failed to parse explanation:', e);
        }

        // Create a dummy session object for the formatter
        const dummySession = {
            session_id: 'temp',
            result: new TypedResultMap(),
            start: new Date(),
            customer_address: '',
            agent_address: null,
            user_id: null,
            conversation_type: 'CALL' as ConversationType
        };

        return formatCustomScore({
            session: dummySession,
            definition: def,
            rawValue,
            explanation: explanation || undefined,
            teamCustomCodes
        });
    };

    const renderMetricValue = (def: CustomDefinition) => {
        const formattedScore = getFormattedMetric(def);
        
        if (!formattedScore) {
            return renderMetricBox(def, '-', {
                main: '#374151',
                bg: '#f3f4f6'
            }, null, null);
        }

        // Determine icon based on value type and positivity
        let icon: React.ReactNode = null;
        if (formattedScore.displayValue === 'Yes') {
            icon = <Check size={12} />;
        } else if (formattedScore.displayValue === 'No') {
            icon = <X size={12} />;
        } else if (formattedScore.displayValue.includes('%')) {
            if (formattedScore.isPositive === true) {
                icon = <ChevronUp size={12} />;
            } else if (formattedScore.isPositive === false) {
                icon = <ChevronDown size={12} />;
            }
        }

        return renderMetricBox(
            def, 
            formattedScore.displayValue, 
            {
                main: formattedScore.textColor,
                bg: formattedScore.backgroundColor
            }, 
            formattedScore.explanation || null, 
            icon
        );
    };

    const renderMetricBox = (def: CustomDefinition, displayValue: React.ReactNode, colors: { main: string, bg: string }, explanation: string | null, icon: React.ReactNode) => {
        const hasTooltip = explanation || def.description;
        const isHovered = hoveredMetric === def.metric_idx;

        return (
            <>
                <div 
                    ref={(el) => {
                        if (def.metric_idx !== null) {
                            elementRefs.current[def.metric_idx] = el;
                        }
                    }}
                    key={def.metric_idx}
                    className="relative inline-flex items-center h-6 px-2 rounded-md"
                    style={{ 
                        backgroundColor: colors.bg,
                        cursor: hasTooltip ? 'help' : 'default',
                        transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={() => hasTooltip && def.metric_idx !== null && setHoveredMetric(def.metric_idx)}
                    onMouseLeave={() => hasTooltip && setHoveredMetric(null)}
                >
                    <div className="flex items-center gap-1.5">
                        <Typography 
                            variant="smallParagraph"
                            color="#555"
                            className="font-medium whitespace-nowrap"
                        >
                            {def.label}
                        </Typography>
                        <Typography 
                            variant="smallParagraph"
                            fontWeight="600"
                            color={colors.main}
                            className="flex items-center gap-0.5 whitespace-nowrap"
                        >
                            {icon && <span className="flex items-center">{icon}</span>}
                            {displayValue}
                        </Typography>
                    </div>
                </div>
                
                {/* Fixed positioned tooltip with same width as element */}
                {hasTooltip && isHovered && def.metric_idx !== null && elementRefs.current[def.metric_idx] && (
                    <div 
                        className="fixed bg-white text-black rounded shadow-lg border border-gray-200 z-[9999] pointer-events-none p-2"
                        style={{ 
                            top: elementRefs.current[def.metric_idx]!.getBoundingClientRect().top - 8, // Position above with small gap
                            left: elementRefs.current[def.metric_idx]!.getBoundingClientRect().left,
                            width: Math.max(elementRefs.current[def.metric_idx]!.getBoundingClientRect().width, 180), // Match score width but min 180px
                            transform: 'translateY(-100%)', // Move fully above the element
                            filter: 'drop-shadow(0 4px 6px rgb(0 0 0 / 0.1))'
                        }}
                    >
                        <Typography variant="smallParagraph" color="#666" className="text-xs leading-tight">
                            {explanation || def.description}
                        </Typography>
                        {/* Small arrow pointing down to the score */}
                        <div 
                            className="absolute bg-white border-r border-b border-gray-200"
                            style={{
                                left: '12px',
                                bottom: '-4px',
                                width: '8px',
                                height: '8px',
                                transform: 'rotate(45deg)'
                            }}
                        />
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="flex flex-wrap items-center gap-2 max-w-full overflow-visible">
            {customDefinitions.map((def) => {
                return renderMetricValue(def);
            })}
        </div>
    );
}; 